import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { X, Sparkles, Phone } from "lucide-react";
import LeadFormEmbed from "@/components/LeadFormEmbed";
import { business } from "@/lib/business-data";

interface QuoteFormValue {
  openQuoteForm: () => void;
  closeQuoteForm: () => void;
  /**
   * True while the modal is mounted. The hero unmounts its own copy of the
   * form while the modal is open so only one instance of the widget iframe
   * (which carries a fixed element id) exists in the document at a time.
   */
  isQuoteFormOpen: boolean;
}

const QuoteFormContext = createContext<QuoteFormValue>({
  openQuoteForm: () => {},
  closeQuoteForm: () => {},
  isQuoteFormOpen: false,
});

export const useQuoteForm = () => useContext(QuoteFormContext);

export const QuoteFormProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const openQuoteForm = useCallback(() => setOpen(true), []);
  const closeQuoteForm = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const modal = open
    ? createPortal(
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="quote-form-title"
          className="fixed inset-0 z-[9999] flex items-start sm:items-center justify-center overflow-y-auto p-4 sm:p-6"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeQuoteForm();
          }}
          style={{ animation: "quote-fade-in 200ms ease-out both" }}
        >
          <div aria-hidden="true" className="fixed inset-0 bg-black/60 backdrop-blur-sm" />

          <div
            className="relative my-auto w-full max-w-[520px] rounded-2xl bg-card shadow-2xl"
            style={{
              boxShadow:
                "0 30px 90px -20px hsl(var(--accent-dark) / 0.65), 0 0 0 1px hsl(var(--primary) / 0.15)",
              animation: "quote-pop-in 340ms cubic-bezier(0.33, 1, 0.68, 1) both",
            }}
          >
            <button
              onClick={closeQuoteForm}
              aria-label="Close estimate form"
              className="absolute -top-3 -right-3 z-10 inline-flex h-10 w-10 items-center justify-center rounded-full bg-card text-foreground shadow-lg ring-1 ring-border hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <X className="h-5 w-5" />
            </button>

            {/* Same header treatment as the hero card, so the two read as one component. */}
            <div className="px-5 pt-6 pb-4 sm:px-6 sm:pt-7">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 border border-primary/25 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-primary mb-3">
                <Sparkles className="h-3 w-3" /> Free Estimate
              </span>
              <h2 id="quote-form-title" className="font-heading text-2xl font-bold text-foreground">
                Get Your Free Estimate
              </h2>
              <p className="text-muted-foreground text-sm mt-1.5">
                Tell us about the job and we'll get back to you with clear next steps.
              </p>
            </div>

            <div className="px-5 pb-5 sm:px-6 sm:pb-6">
              <LeadFormEmbed />
            </div>

            <div className="px-5 pb-6 sm:px-6 -mt-1">
              <a
                href={business.phoneHref}
                className="flex items-center justify-center gap-2 rounded-xl border border-border bg-muted/60 py-3 text-sm font-semibold text-foreground hover:bg-muted transition-colors"
              >
                <Phone className="h-4 w-4 text-primary" /> Prefer to talk? Call {business.phone}
              </a>
            </div>
          </div>

          <style>{`
            @keyframes quote-fade-in { from { opacity: 0; } to { opacity: 1; } }
            @keyframes quote-pop-in {
              from { opacity: 0; transform: translateY(14px) scale(0.97); }
              to   { opacity: 1; transform: translateY(0) scale(1); }
            }
            @media (prefers-reduced-motion: reduce) {
              [role="dialog"], [role="dialog"] > div { animation: none !important; }
            }
          `}</style>
        </div>,
        document.body
      )
    : null;

  return (
    <QuoteFormContext.Provider value={{ openQuoteForm, closeQuoteForm, isQuoteFormOpen: open }}>
      {children}
      {modal}
    </QuoteFormContext.Provider>
  );
};
